// @ts-strict-ignore
import { AfterViewInit, Directive, ElementRef, HostListener, Input } from '@angular/core';
import { AbstractControl, NgControl, ValidationErrors, Validators } from '@angular/forms';
import { FormatNumberService } from '../../../services/format-number.service';
import { DIGITS_INFO_HIGH_DETAIL } from '../../../app.constants';

interface CustomNumberOptions {
  maxDecimals?: number;
  validateDecimals?: boolean;
}
@Directive({
    selector: 'input[customNumber]',
    standalone: false
})
export class CustomNumberDirective implements AfterViewInit {
  @Input() allowEmpty: boolean = false;
  @Input('value') set value(value: string | null) {
    this._value = value;
    this._formatValue(value);
  }

  @Input() set customNumber({ maxDecimals, validateDecimals }: CustomNumberOptions) {
    if (validateDecimals !== undefined) {
      this._validateDecimals = validateDecimals;
    }
    this._maxDecimals = maxDecimals ?? 20;
  }

  get value(): string | null {
    return this._value;
  }

  @Input() set formControl(control: AbstractControl) {
    this._formControl = control;
    const decimalValidator = (ctrl: AbstractControl): ValidationErrors | null => {
      if (this._validateDecimals && this._numberOfDecimalPlaces(ctrl.value?.toString()) > this._maxDecimals) {
        return { decimal: { maxDecimals: this._maxDecimals } };
      }
    };

    control.setValidators(Validators.compose([control.validator, decimalValidator]));
  }

  @HostListener('blur')
  onBlur() {
    this._formatValue(this._value);
  }

  @HostListener('keydown', ['$event.key'])
  onKeyDown(key) {
    if (key === '+') return false;
  }

  @HostListener('focus')
  onFocus() {
    this._unFormatValue();
  }

  @HostListener('input', ['$event.target.value'])
  onInput(value) {
    this._value = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    this.onChange(this.allowEmpty ? this._value : Number(this._value));
  }

  private _formControl: AbstractControl;
  private _maxDecimals: number;
  private _validateDecimals: boolean = true;
  private _value: string | null;

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private FormatNumberService: FormatNumberService,
    public ngControl: NgControl
  ) {
    ngControl.valueAccessor = this;
  }
  ngAfterViewInit(): void {
    if (!this._formControl) {
      throw new Error('CustomNumberDirective requires formControl to be set.');
    }
  }

  onChange(value: any): void {}

  registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched() {}

  writeValue(value: any) {
    this._value = value;
    this._formatValue(this._value);
  }

  private _formatValue(value: string | null) {
    if (value !== null && value !== '' && !isNaN(Number(value))) {
      this.elementRef.nativeElement.value = this.FormatNumberService.format(value, DIGITS_INFO_HIGH_DETAIL);
    } else {
      this.elementRef.nativeElement.value = '';
    }

    if (this.ngControl) {
      this.ngControl.control?.markAsTouched();
    }
  }

  private _unFormatValue() {
    const value = this.elementRef.nativeElement.value;
    this._value = this.FormatNumberService.parse(value);
    if (value) {
      this.elementRef.nativeElement.value = this._value;
    } else {
      this.elementRef.nativeElement.value = '';
    }
  }

  private _numberOfDecimalPlaces(number: string) {
    if (number === undefined) return 0;

    const match = number.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
    if (!match || match[0] === '0' || match.length === 1) {
      return 0;
    }
    return match[1]?.length;
  }
}
